import { Chain } from '@1delta/chain-registry'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { MulticallRetryFunction } from '../../types/providers'
import { apyToAprPercent, parseRawAmount } from '../../utils/parsing'
import { curatorNameFromVaultName } from '../curatorName'
import {
  eulerEntityCuratorName,
  fetchEulerEntityNames,
} from './entityLabels'
import { deriveConvertToAssets } from '../sharePrice'
import { EulerEarnVault, EulerEarnVaults } from './types'

const DECIMALS_ABI = [
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint8' }],
  },
] as const

/**
 * Read each earn vault's own `decimals()` on-chain. The Earn subgraph
 * doesn't surface the share-token decimals, and meta-vaults can apply a
 * decimals offset (18-dec shares over 6-dec USDC), so we can't assume
 * share == asset. Best-effort: a failed read just leaves the vault out of
 * the map, and the parser falls back to asset decimals (offset-free).
 */
export async function fetchShareDecimals(
  chainId: string,
  vaultAddrs: string[],
  multicallRetry?: MulticallRetryFunction,
): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  if (!multicallRetry || vaultAddrs.length === 0) return out
  try {
    const res = await multicallRetry({
      chain: chainId,
      calls: vaultAddrs.map((address) => ({
        address,
        name: 'decimals',
        params: [],
      })),
      abi: vaultAddrs.map(() => DECIMALS_ABI),
      allowFailure: true,
      maxRetries: 2,
    })
    vaultAddrs.forEach((a, i) => {
      const d = Number(res?.[i])
      if (Number.isFinite(d)) out.set(a.toLowerCase(), d)
    })
  } catch {
    // best-effort — parser falls back to asset decimals
  }
  return out
}

/**
 * Goldsky subgraph endpoints for Euler Earn deployments, keyed by chainId.
 *
 * Euler V2 publishes one subgraph per chain at `euler-v2-<chain>`. The
 * `eulerEarnVaults` collection is the Earn (meta-vault) layer; the
 * companion `eulerVaults` collection is the underlying EVK lending
 * vaults (handled by the lender path, not this fetcher).
 *
 * Source: https://docs.euler.finance/developers/data-querying/subgraphs/
 */
const EULER_EARN_PROJECT = 'project_cm4iagnemt1wp01xn4gh1agft'
const eulerSubgraphUrl = (slug: string) =>
  `https://api.goldsky.com/api/public/${EULER_EARN_PROJECT}/subgraphs/${slug}/latest/gn`

const EULER_EARN_SUBGRAPH_URLS: Record<string, string> = {
  [Chain.ETHEREUM_MAINNET]: eulerSubgraphUrl('euler-v2-mainnet'),
  [Chain.BASE]: eulerSubgraphUrl('euler-v2-base'),
  [Chain.ARBITRUM_ONE]: eulerSubgraphUrl('euler-v2-arbitrum'),
  [Chain.SONIC_MAINNET]: eulerSubgraphUrl('euler-v2-sonic'),
  [Chain.SWELLCHAIN]: eulerSubgraphUrl('euler-v2-swell'),
  [Chain.UNICHAIN]: eulerSubgraphUrl('euler-v2-unichain'),
  [Chain.BNB_SMART_CHAIN_MAINNET]: eulerSubgraphUrl('euler-v2-bsc'),
  [Chain.AVALANCHE_C_CHAIN]: eulerSubgraphUrl('euler-v2-avalanche'),
}

/** True when the chain has an Euler Earn Goldsky subgraph configured. */
export const hasEulerEarnVaultSubgraph = (chainId: string): boolean =>
  chainId in EULER_EARN_SUBGRAPH_URLS

/** Goldsky subgraph URL for a chain, or undefined when none is configured. */
export const getEulerEarnSubgraphUrl = (chainId: string): string | undefined =>
  EULER_EARN_SUBGRAPH_URLS[chainId]

/**
 * Pulls every Earn vault with its strategies. The nested `eulerVault`
 * relation on each strategy is **deliberately omitted** — many strategies
 * point at EVKs the subgraph hasn't fully indexed (or that the relation
 * resolver can't materialize) and asking for `strategies { eulerVault { ... } }`
 * makes the whole response error out with `Null value resolved for
 * non-null field eulerVault`. We re-join against the flat `strategy`
 * Bytes field (address pointer) in a second query.
 */
const EULER_EARN_VAULTS_QUERY = `
{
  eulerEarnVaults(first: 200, skip: 0) {
    id
    name
    symbol
    asset
    owner
    curator
    guardian
    feeReceiver
    performanceFee
    totalAssets
    totalShares
    totalSupply
    strategies {
      id
      strategy
      allocatedAssets
    }
  }
}
`

/**
 * Companion query. Pulls `decimals` (which Earn vaults inherit by ERC-4626
 * convention but the Earn subgraph doesn't surface), live `supplyApy`
 * (RAY-scaled: `1e27 = 100 %`) used for the weighted Earn APY, and
 * `cash` — the immediate-withdrawable underlying balance per EVK, used
 * to compute true Earn-vault liquidity (instead of the optimistic
 * `totalAssets` ceiling).
 *
 * We filter by `asset_in` rather than just `id_in` so that Earn vaults
 * with zero strategies still get the correct decimals via the asset
 * map.
 */
const EVK_BY_ASSETS_QUERY = `
query EvkByAssets($assets: [Bytes!]!) {
  eulerVaults(first: 1000, where: { asset_in: $assets }) {
    id
    decimals
    asset
    state {
      supplyApy
      cash
    }
  }
}
`

interface SubgraphStrategy {
  id: string
  strategy: string | null
  allocatedAssets: string | null
}

interface SubgraphEulerEarnVault {
  id: string
  name: string | null
  symbol: string | null
  asset: string | null
  owner: string | null
  curator: string | null
  guardian: string | null
  feeReceiver: string | null
  performanceFee: string | number | null
  totalAssets: string | null
  totalShares: string | null
  totalSupply: string | null
  strategies?: SubgraphStrategy[] | null
}

interface SubgraphEvk {
  id: string
  decimals: string | number | null
  asset: string | null
  state: { supplyApy: string | null; cash: string | null } | null
}

/**
 * Per-EVK index used to derive Earn-vault APY and liquidity.
 *
 * `bySupplyApyRay` and `byCash` are keyed by lowercased EVK address —
 * the same value the Earn subgraph stores as `Strategy.strategy`.
 * `decimalsByAsset` is a per-asset rollup (decimals are identical for
 * every EVK over the same asset, by ERC-4626 convention).
 */
interface EvkIndex {
  bySupplyApyRay: Map<string, bigint>
  byCash: Map<string, bigint>
  decimalsByAsset: Map<string, number>
}

const ZERO = 0n
const RAY_TO_PERCENT = 1e25 // 1e27 (RAY, = 100 %) ÷ 100 = 1e25 per percent point.

/** Resolve a Bytes-typed address field. The subgraph returns flat
 *  lowercased hex strings; we normalize defensively in case that
 *  changes. */
function readAddress(field: string | null | undefined): string | undefined {
  if (!field) return undefined
  return field.toLowerCase()
}

/** Performance fee is a 1e18-scaled fraction in the Euler Earn
 *  subgraph (e.g. `5e16` = 5 %). Normalize to percent for display. */
function parseVaultFee(v: SubgraphEulerEarnVault): number {
  if (v.performanceFee == null) return 0
  const n = Number(v.performanceFee)
  if (!Number.isFinite(n)) return 0
  // Heuristic mirrors the other vault parsers: <1 → decimal (0.05 =
  // 5 %); ≥1 and ≤100 → already percent; >100 → 1e18-scaled.
  if (n < 1) return n * 100
  if (n > 100) return n / 1e16
  return n
}

function safeBigInt(v: string | null | undefined): bigint {
  if (v == null || v === '') return ZERO
  try {
    return BigInt(v)
  } catch {
    return ZERO
  }
}

/**
 * Weighted supply rate (APR, in percent) across all the Earn vault's
 * strategies, net of the Earn vault's performance fee. The per-EVK
 * `supplyApy` is a compounded APY; it's converted to APR before return so
 * this matches the API path and the rest of the pipeline.
 *
 * Formula: `Σ(allocated_i × evkSupplyApy_i_RAY) / totalAssets` — using
 * `totalAssets` as the denominator (not Σallocated) so that idle cash
 * correctly dilutes the headline rate the user actually receives.
 *
 * Returns 0 when:
 *  - the Earn vault has no indexed strategies (new / empty vaults);
 *  - none of its strategy EVKs are present in `evkIndex` (subgraph hasn't
 *    indexed them yet — partial response);
 *  - `totalAssets` is 0.
 *
 * The performance-fee deduction is applied *after* the weighted average,
 * mirroring how upstream Morpho/Silo report `supplyRate` as net-of-fee.
 */
function computeWeightedSupplyApy(
  strategies: SubgraphStrategy[] | null | undefined,
  totalAssetsRaw: string | null | undefined,
  feePercent: number,
  evkIndex: EvkIndex,
): number {
  if (!strategies?.length) return 0
  const totalAssets = safeBigInt(totalAssetsRaw)
  if (totalAssets === ZERO) return 0

  let weightedRay = ZERO
  for (const s of strategies) {
    const evk = s.strategy?.toLowerCase()
    if (!evk) continue
    const apyRay = evkIndex.bySupplyApyRay.get(evk)
    if (apyRay == null) continue
    const allocated = safeBigInt(s.allocatedAssets)
    if (allocated === ZERO) continue
    weightedRay += allocated * apyRay
  }
  if (weightedRay === ZERO) return 0

  // weightedRay scale: allocated (raw underlying wei) × apy (RAY). Dividing
  // by totalAssets (also raw underlying wei) leaves us in pure RAY scale.
  // We do the percent conversion in Number since the dynamic range after
  // dividing by totalAssets sits comfortably inside f64.
  const apyRayPerAsset = weightedRay / totalAssets
  // The EVK `supplyApy` is a compounded APY; our `supplyRate` is APR — convert
  // (matches Morpho's `apyToApr` and the euler-earn API path, which were
  // already converting; the subgraph path was the inconsistent one).
  const grossPercent = apyToAprPercent(Number(apyRayPerAsset) / RAY_TO_PERCENT)
  const netPercent = grossPercent * (1 - feePercent / 100)
  return Number.isFinite(netPercent) ? netPercent : 0
}

/**
 * Real immediate-withdraw liquidity (in raw underlying wei), accounting
 * for per-EVK cash caps.
 *
 * Formula: `idle_cash + Σ min(allocated_i, evk_cash_i)`. Idle is the
 * portion of `totalAssets` not yet routed into any strategy — it sits in
 * the Earn vault and is always withdrawable. Per-strategy contribution
 * is capped by the underlying EVK's `cash` (raw underlying not currently
 * lent out): when an EVK is at 100 % utilization, the Earn vault can't
 * pull from it even though the allocation is non-zero.
 *
 * **Unindexed-EVK fallback.** When a strategy points at an EVK that the
 * subgraph hasn't indexed (no `cash` available), we count the full
 * allocation toward liquidity — optimistic, but keeps the result no
 * worse than the previous `totalAssets`-ceiling behavior. The figure is
 * never larger than `totalAssets`.
 *
 * Defaults to `totalAssets` when the EVK index is completely empty
 * (the companion query failed) — same optimistic ceiling as before, so
 * a partial fetch never silently *under*-reports liquidity.
 */
function computeRealLiquidity(
  strategies: SubgraphStrategy[] | null | undefined,
  totalAssetsRaw: string | null | undefined,
  evkIndex: EvkIndex,
): bigint {
  const totalAssets = safeBigInt(totalAssetsRaw)
  if (totalAssets === ZERO) return ZERO

  // No EVK data at all → fall back to the optimistic ceiling.
  if (evkIndex.byCash.size === 0 && evkIndex.bySupplyApyRay.size === 0) {
    return totalAssets
  }

  if (!strategies?.length) {
    // Earn vault has no strategies indexed yet — everything is idle by
    // definition, withdraw cap = totalAssets.
    return totalAssets
  }

  let allocSum = ZERO
  let withdrawable = ZERO
  for (const s of strategies) {
    const alloc = safeBigInt(s.allocatedAssets)
    if (alloc === ZERO) continue
    allocSum += alloc
    const evk = s.strategy?.toLowerCase()
    const cash = evk ? evkIndex.byCash.get(evk) : undefined
    // Unindexed EVK: optimistic — assume the full allocation is
    // immediately withdrawable. Indexed: cap by EVK cash.
    withdrawable += cash == null ? alloc : alloc < cash ? alloc : cash
  }
  // `totalAllocated` from the subgraph mirrors Σallocated, but recomputing
  // from strategies keeps the result self-consistent if the entities
  // are momentarily out of sync.
  const idle = totalAssets > allocSum ? totalAssets - allocSum : ZERO
  const result = idle + withdrawable
  // Hard cap: cannot exceed totalAssets (defensive — small overshoot is
  // possible if the subgraph snapshot has drifted between entities).
  return result > totalAssets ? totalAssets : result
}

function parseVault(
  v: SubgraphEulerEarnVault,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  evkIndex: EvkIndex,
  shareDecimalsByVault: Map<string, number>,
  entityNames: Map<string, string>,
): EulerEarnVault | null {
  const address = (v?.id ?? '').toLowerCase()
  const assetAddr = (v?.asset ?? '').toLowerCase()
  if (!address || !assetAddr) return null

  const assetMeta = tokenList[assetAddr]
  // ASSET decimals resolution order:
  //   1. EVK index by asset — subgraph-authoritative, covers anything
  //      that's part of an Euler V2 deployment (which is every Earn
  //      vault's underlying by definition).
  //   2. Token list — covers chains/assets with the EVK relation missing
  //      but a curated token list available.
  //   3. Fallback 18 — last resort; logged-safe but produces a 10^12
  //      blowup on USDC-style assets, so prefer the first two.
  const assetDecimals = Number(
    evkIndex.decimalsByAsset.get(assetAddr) ?? assetMeta?.decimals ?? 18,
  )
  // SHARE decimals — the earn vault's own `decimals()`. The Earn subgraph
  // doesn't surface it, and meta-vaults can apply a decimals offset
  // (e.g. Clearstar's 18-dec shares over 6-dec USDC). Read on-chain;
  // fall back to asset decimals (offset-free) when the read is unavailable.
  const decimals = shareDecimalsByVault.get(address) ?? assetDecimals

  const totalAssetsRaw = v.totalAssets?.toString() ?? '0'
  // totalAssets is underlying-denominated → format with asset decimals.
  const totalAssetsFormatted = Number(
    parseRawAmount(totalAssetsRaw, assetDecimals),
  )

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const priceUsd = prices[oracleKey] ?? prices[assetAddr] ?? 0

  const fee = parseVaultFee(v)
  const supplyRate = computeWeightedSupplyApy(
    v.strategies,
    totalAssetsRaw,
    fee,
    evkIndex,
  )

  // `totalShares` is the older field name; `totalSupply` mirrors it
  // on newer schema versions. Prefer the latter when present.
  const totalSupplyRaw =
    v.totalSupply?.toString() ?? v.totalShares?.toString() ?? '0'
  // `convertToAssets(10^shareDecimals)` — probe with SHARE decimals; the
  // result is asset-scaled (formatted later with `assetDecimals`).
  const convertToAssets = deriveConvertToAssets(
    totalAssetsRaw,
    totalSupplyRaw,
    decimals,
  )

  // Real immediate-withdraw liquidity: idle + Σ min(allocated_i, evk_cash_i).
  // Falls back to totalAssets when the EVK index is empty (companion
  // query failed) — same optimistic ceiling as the other earn-vault
  // fetchers, so a partial fetch never silently under-reports.
  const liquidityBig = computeRealLiquidity(
    v.strategies,
    totalAssetsRaw,
    evkIndex,
  )
  const liquidityRaw = liquidityBig.toString()
  const liquidityFormatted = Number(parseRawAmount(liquidityRaw, assetDecimals))

  return {
    address,
    underlying: assetAddr,
    symbol: v.symbol ?? '',
    name: v.name ?? '',
    decimals,
    assetDecimals,
    // Visibility is Data-API-only; subgraph-sourced vaults default to listed.
    whitelisted: true,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    convertToAssets,
    supplyRate,
    rewardsRate: 0,
    depositRate: supplyRate,
    fee,
    owner: readAddress(v.owner),
    curator: readAddress(v.curator),
    // Euler's entity registry names the curator by ADDRESS — authoritative
    // when it hits; the vault-name parse only covers what it misses.
    curatorName:
      eulerEntityCuratorName(
        entityNames,
        readAddress(v.curator),
        readAddress(v.owner),
      ) ?? curatorNameFromVaultName(v.name, assetMeta?.symbol),
    guardian: readAddress(v.guardian),
    feeRecipient: v.feeReceiver?.toLowerCase() || undefined,
    asset: assetMeta,
    priceUsd: priceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd: totalAssetsFormatted * priceUsd,
    liquidity: liquidityRaw,
    liquidityFormatted,
    liquidityUsd: liquidityFormatted * priceUsd,
  }
}

async function postQuery(url: string, body: object): Promise<any> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(
      `Euler Earn vaults subgraph failed: ${response.status} - ${response.statusText}`,
    )
  }
  return response.json()
}

async function fetchEvkIndex(url: string, assets: string[]): Promise<EvkIndex> {
  const empty: EvkIndex = {
    bySupplyApyRay: new Map(),
    byCash: new Map(),
    decimalsByAsset: new Map(),
  }
  if (assets.length === 0) return empty

  // Companion call is best-effort: if the EVK query errors (subgraph
  // partial-resolve, transient 5xx, schema drift on the `state` relation)
  // we still want to ship Earn-vault data with `supplyRate = 0`,
  // decimals from the token list, and liquidity falling back to the
  // optimistic totalAssets ceiling — instead of failing the whole fetch.
  let json: any
  try {
    json = await postQuery(url, {
      query: EVK_BY_ASSETS_QUERY,
      variables: { assets },
    })
  } catch {
    return empty
  }

  const items: SubgraphEvk[] = json?.data?.eulerVaults ?? []
  for (const e of items) {
    const id = e?.id?.toLowerCase()
    const asset = e?.asset?.toLowerCase()
    if (!id || !asset) continue
    const decimals = Number(e.decimals ?? NaN)
    if (Number.isFinite(decimals) && !empty.decimalsByAsset.has(asset)) {
      empty.decimalsByAsset.set(asset, decimals)
    }
    const apyRaw = e.state?.supplyApy
    if (apyRaw != null) {
      empty.bySupplyApyRay.set(id, safeBigInt(apyRaw))
    }
    const cashRaw = e.state?.cash
    if (cashRaw != null) {
      empty.byCash.set(id, safeBigInt(cashRaw))
    }
  }
  return empty
}

/**
 * Fetches Euler Earn vaults from the Goldsky subgraph for chains
 * configured in `EULER_EARN_SUBGRAPH_URLS`.
 *
 * Two-phase fetch:
 *   1. `eulerEarnVaults` + flat strategies (drops the nested `eulerVault`
 *      relation, which throws null-violation errors when un-indexed).
 *   2. `eulerVaults` filtered to the assets discovered in step 1, used
 *      both for `decimals` (Earn subgraph doesn't expose it) and live
 *      `state.supplyApy` (used to compute the weighted Earn-vault APY).
 *
 * Phase 2 failures degrade gracefully — Earn-vault rows are still
 * returned with `supplyRate = 0` and `decimals` from the token list.
 */
export async function fetchEulerEarnVaultsFromSubgraph(
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  multicallRetry?: MulticallRetryFunction,
): Promise<EulerEarnVaults> {
  const url = EULER_EARN_SUBGRAPH_URLS[chainId]
  if (!url) return {}

  const json: any = await postQuery(url, { query: EULER_EARN_VAULTS_QUERY })
  const items: SubgraphEulerEarnVault[] = json?.data?.eulerEarnVaults ?? []
  if (items.length === 0) return {}

  const assets = new Set<string>()
  const vaultAddrs: string[] = []
  for (const v of items) {
    const a = v?.asset?.toLowerCase()
    if (a) assets.add(a)
    const id = v?.id
    if (id) vaultAddrs.push(id)
  }
  // EVK index (asset decimals + apy + cash), share decimals and the curator
  // entity registry run together.
  const [evkIndex, shareDecimalsByVault, entityNames] = await Promise.all([
    fetchEvkIndex(url, [...assets]),
    fetchShareDecimals(chainId, vaultAddrs, multicallRetry),
    fetchEulerEntityNames(chainId),
  ])

  const out: EulerEarnVaults = {}
  for (const v of items) {
    const parsed = parseVault(
      v,
      chainId,
      prices,
      tokenList,
      evkIndex,
      shareDecimalsByVault,
      entityNames,
    )
    if (parsed) out[parsed.address] = parsed
  }
  return out
}
